import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, Briefcase, PlusCircle, User, Bell } from "lucide-react";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNotifications } from "@/hooks/useNotifications";
import { Badge } from "@/components/ui/badge";

const MobileBottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | undefined>();
  const [userRole, setUserRole] = useState<"customer" | "professional" | "admin" | "moderator" | null>(null);
  const { unreadCount } = useNotifications(userId);

  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUserId(session.user.id);
        
        const { data: roleData } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", session.user.id)
          .single();
        
        if (roleData) {
          setUserRole(roleData.role);
        }
      }
    };
    checkUser();
  }, []);

  const handleNewJobClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (userId) {
      if (userRole === "professional") {
        navigate("/jobs");
      } else {
        navigate("/dashboard", { state: { openCreateDialog: true } });
      }
    } else {
      navigate("/auth?register=true&role=customer");
    }
  };
  
  const leftNavItems = [
    { path: "/", icon: Home, label: "Αρχική" },
    { path: "/dashboard", icon: Briefcase, label: "Πίνακας" },
  ];

  const rightNavItems = [
    { path: "/notifications", icon: Bell, label: "Ειδοποιήσεις", badge: unreadCount },
    { path: "/profile", icon: User, label: "Προφίλ" },
  ];

  // Don't show on auth page
  if (location.pathname === "/auth" || location.pathname.startsWith("/auth/")) {
    return null;
  }

  // Hide on home page when user is not logged in
  if (location.pathname === "/" && !userId) {
    return null;
  }

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-background border-t border-border lg:hidden safe-area-inset-bottom">
      <div className="flex items-center justify-around h-16 relative">
        {/* Left Navigation Items */}
        {leftNavItems.map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;
          
          return (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                "flex flex-col items-center justify-center transition-colors flex-1",
                isActive ? "text-primary" : "text-muted-foreground"
              )}
            >
              <Icon className={cn("w-5 h-5 mb-1", isActive && "text-primary")} />
              <span className="text-[10px] font-medium truncate max-w-[60px]">
                {item.label}
              </span>
            </Link>
          );
        })}
        
        {/* Center FAB for New Job */}
        <button
          onClick={handleNewJobClick}
          className="flex flex-col items-center justify-center flex-1"
        >
          <div className="bg-primary text-primary-foreground w-14 h-14 rounded-full flex items-center justify-center shadow-lg -mt-6 hover:bg-primary/90 transition-colors">
            <PlusCircle className="w-6 h-6" />
          </div>
          <span className="text-[10px] mt-1 font-medium text-primary">
            {userRole === "professional" ? "Εργασίες" : "Νέα"}
          </span>
        </button>

        {/* Right Navigation Items */}
        {rightNavItems.map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;
          
          return (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                "flex flex-col items-center justify-center transition-colors flex-1",
                isActive ? "text-primary" : "text-muted-foreground"
              )}
            >
              <div className="relative mb-1">
                <Icon className={cn("w-5 h-5", isActive && "text-primary")} />
                {(item.badge ?? 0) > 0 && (
                  <Badge 
                    variant="destructive" 
                    className="absolute -top-1 -right-2 h-4 w-4 flex items-center justify-center p-0 text-[10px]"
                  >
                    {item.badge! > 9 ? "9+" : item.badge}
                  </Badge>
                )}
              </div>
              <span className="text-[10px] font-medium truncate max-w-[60px]">
                {item.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default MobileBottomNav;
